import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit2, Trash2, TrendingDown, DollarSign, Upload, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import api from '@/api/api';
import BankSelector from '@/components/BankSelector';
import Pagination from '@/components/Pagination';

const ExpenseManagement = () => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedBank, setSelectedBank] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    amount: '',
    category: '',
    category_id: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    payment_method: 'Cash'
  });
  const [editingId, setEditingId] = useState(null);
  const [expenseStats, setExpenseStats] = useState({ total_amount: 0, total_count: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);

  // ── Bulk Upload state ─────────────────────────────────────────────────────
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const bulkFileRef = useRef(null);

  useEffect(() => {
    fetchExpenses();
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/accounts/expenses?page=${currentPage}&limit=${itemsPerPage}`);
      // axios interceptor auto-unwraps: response.data = items array, response.pagination = {total, stats, ...}
      setExpenses(Array.isArray(response.data) ? response.data : []);
      if (response.pagination) {
        setExpenseStats(response.pagination.stats || { total_amount: 0, total_count: 0 });
        setTotalItems(response.pagination.total || 0);
      }
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/accounts/expense-categories');
      setCategories(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate bank selection for non-cash payments
    if (formData.payment_method !== 'Cash' && !selectedBank) {
      alert('Please select a bank for non-cash payment');
      return;
    }
    
    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount),
        expense_date: formData.date, // Map frontend 'date' to backend 'expense_date'
        payment_mode: formData.payment_method, // Map frontend 'payment_method' to backend 'payment_mode'
        bank_id: formData.payment_method !== 'Cash' ? selectedBank : null
      };
      if (editingId) {
        await api.put(`/accounts/expenses/${editingId}`, data);
      } else {
        await api.post('/accounts/expenses', data);
      }
      fetchExpenses();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving expense:', error);
      const detail = error?.response?.data?.detail || error?.message || 'Failed to save expense';
      alert(typeof detail === 'string' ? detail : 'Failed to save expense');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this expense record?')) {
      try {
        await api.delete(`/accounts/expenses/${id}`);
        fetchExpenses();
      } catch (error) {
        console.error('Error deleting expense:', error);
        alert('Failed to delete expense');
      }
    }
  };

  const handleEdit = (expense) => {
    setFormData({
      title: expense.title,
      amount: expense.amount,
      category: expense.category,
      category_id: expense.category_id || '',
      description: expense.description || '',
      date: (expense.expense_date || expense.date || '').slice(0, 10),
      payment_method: expense.payment_method || expense.payment_mode || 'Cash'
    });
    setSelectedBank(expense.bank_id || '');
    setEditingId(expense.id);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setSelectedBank('');
    setFormData({
      title: '',
      amount: '',
      category: '',
      category_id: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      payment_method: 'Cash'
    });
    setEditingId(null);
  };

  // ── Bulk Upload helpers ────────────────────────────────────────────────
  const BULK_EXPENSE_HEADERS = [
    'expense_date', 'category', 'description', 'amount',
    'payment_mode', 'bank_name', 'supplier_name',
    'reference_number', 'paid_by', 'title'
  ];

  const downloadBulkExpenseTemplate = () => {
    const sample = [
      BULK_EXPENSE_HEADERS.join(','),
      '15/06/2025,Electricity,June electricity bill,5000,Cash,,,ELEC-2025-06,Admin,Electricity Bill',
      '2025-07-01,Rent,Office rent July,15000,UPI,HDFC Main,XYZ Properties,RENT-2025-07,Admin,Office Rent',
      '# Notes: expense_date accepts YYYY-MM-DD or DD/MM/YYYY',
      '# payment_mode: Cash | UPI | Bank Transfer | Cheque | Card',
      '# bank_name required for non-Cash modes (leave blank to use default bank)',
      '# supplier_name optional — if it matches a supplier, their balance increases',
      '# reference_number optional but unique per row — same ref on re-upload will skip',
      '# category auto-creates if not present in master list',
    ].join('\n');
    const blob = new Blob(['\uFEFF' + sample], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_expenses_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // CSV parser that respects quoted fields (so commas inside descriptions
  // don't shift columns — same gotcha that broke bulk admission earlier).
  const parseCSVLine = (line) => {
    const out = [];
    let cur = '';
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQ) {
        if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (ch === '"') { inQ = false; }
        else { cur += ch; }
      } else {
        if (ch === '"') inQ = true;
        else if (ch === ',') { out.push(cur); cur = ''; }
        else cur += ch;
      }
    }
    out.push(cur);
    return out;
  };

  const handleBulkUpload = async () => {
    const file = bulkFileRef.current?.files?.[0];
    if (!file) {
      alert('Please choose a CSV file first.');
      return;
    }
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const text = await file.text();
      const rawLines = text.split(/\r?\n/);
      // Skip empty + comment lines starting with '#'
      const lines = rawLines.filter(l => l.trim() && !l.trim().startsWith('#'));
      if (lines.length < 2) throw new Error('CSV must have a header row and at least one data row.');

      const headers = parseCSVLine(lines[0]).map(h => h.trim().toLowerCase());
      const required = ['expense_date', 'category', 'description', 'amount'];
      const missing = required.filter(r => !headers.includes(r));
      if (missing.length) throw new Error(`Missing required columns: ${missing.join(', ')}`);

      const expenses = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = parseCSVLine(lines[i]);
        const row = {};
        headers.forEach((h, j) => { row[h] = (cols[j] || '').trim(); });
        if (!row.expense_date && !row.category && !row.amount) continue; // blank row
        expenses.push({
          expense_date: row.expense_date,
          category: row.category,
          description: row.description,
          amount: parseFloat(row.amount) || 0,
          payment_mode: row.payment_mode || 'Cash',
          bank_name: row.bank_name || null,
          supplier_name: row.supplier_name || null,
          reference_number: row.reference_number || null,
          paid_by: row.paid_by || null,
          title: row.title || null,
        });
      }

      if (expenses.length === 0) throw new Error('No expense rows found in CSV.');

      // ── Chunked upload (avoids K8s ingress timeout on large batches) ─────
      const CHUNK_SIZE = 25;
      const REQUEST_TIMEOUT_MS = 180_000;
      const agg = { successful: 0, failed: 0, skipped: 0, total: 0, details: [] };
      setBulkProgress({ done: 0, total: expenses.length });

      for (let i = 0; i < expenses.length; i += CHUNK_SIZE) {
        const chunk = expenses.slice(i, i + CHUNK_SIZE);
        try {
          const resp = await api.post(
            '/accounts/expenses/bulk',
            { expenses: chunk },
            { timeout: REQUEST_TIMEOUT_MS }
          );
          const d = resp?.data || resp || {};
          agg.successful += d.successful || 0;
          agg.failed += d.failed || 0;
          agg.skipped += d.skipped || 0;
          agg.total += d.total || chunk.length;
          (d.details || []).forEach(det => {
            agg.details.push({ ...det, row: (det.row || 0) + i });
          });
        } catch (chunkErr) {
          agg.failed += chunk.length;
          agg.total += chunk.length;
          const msg = chunkErr?.response?.data?.detail || chunkErr?.message || 'Chunk request failed';
          chunk.forEach((_, j) => {
            agg.details.push({ row: i + j + 1, status: 'failed', message: String(msg) });
          });
        }
        setBulkProgress({ done: Math.min(i + CHUNK_SIZE, expenses.length), total: expenses.length });
      }
      setBulkResult(agg);
      // Refresh master list + table
      fetchCategories();
      fetchExpenses();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Bulk upload failed';
      setBulkResult({ error: typeof detail === 'string' ? detail : JSON.stringify(detail) });
    } finally {
      setBulkUploading(false);
      setBulkProgress({ done: 0, total: 0 });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  // Use dynamic categories from API
  const expenseCategories = categories.length > 0 
    ? categories.map(c => c.category_name) 
    : ['Salary', 'Utility Bills', 'Maintenance', 'Supplies', 'Equipment', 'Marketing', 'Transportation', 'Rent', 'Insurance', 'Taxes', 'Miscellaneous', 'Other'];

  const handleCategoryChange = (categoryName) => {
    const selectedCategory = categories.find(c => c.category_name === categoryName);
    setFormData({
      ...formData,
      category: categoryName,
      category_id: selectedCategory?.id || ''
    });
  };

  const totalExpense = expenseStats.total_amount || expenses.reduce((sum, expense) => sum + (expense.amount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <TrendingDown className="text-red-600" size={32} />
            Expense Management
          </h1>
          <p className="text-gray-600 mt-1">Track and manage all expenses</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Dialog open={bulkOpen} onOpenChange={(o) => { setBulkOpen(o); if (!o) setBulkResult(null); }}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                className="border-red-600 text-red-700 hover:bg-red-50"
                data-testid="expenses-bulk-upload-btn"
              >
                <Upload size={18} className="mr-2" />
                Bulk Upload
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Bulk Upload Expenses</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
                  <p className="font-semibold">Before you upload:</p>
                  <ul className="list-disc list-inside text-xs mt-1 space-y-0.5">
                    <li>Make sure your tenant has enough Cash / Bank balance — rows are validated against current balance.</li>
                    <li>Same <code>reference_number</code> on re-upload is skipped (safe to retry).</li>
                    <li>Missing categories are auto-created in your master list.</li>
                    <li>Non-Cash rows need <code>bank_name</code> (or a default bank configured).</li>
                  </ul>
                </div>
                <Button
                  variant="outline"
                  onClick={downloadBulkExpenseTemplate}
                  data-testid="expenses-bulk-template-btn"
                >
                  <Download size={16} className="mr-2" />
                  Download CSV Template
                </Button>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CSV File *</label>
                  <input
                    ref={bulkFileRef}
                    type="file"
                    accept=".csv,text/csv"
                    data-testid="expenses-bulk-file-input"
                    className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded file:border-0 file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
                  />
                </div>
                <Button
                  onClick={handleBulkUpload}
                  disabled={bulkUploading}
                  data-testid="expenses-bulk-submit-btn"
                  className="bg-red-600 hover:bg-red-700 disabled:opacity-50"
                >
                  {bulkUploading
                    ? (bulkProgress.total > 0 ? `Uploading ${bulkProgress.done} / ${bulkProgress.total}…` : 'Uploading…')
                    : 'Upload Expenses'}
                </Button>

                {bulkUploading && bulkProgress.total > 0 && (
                  <div className="w-full bg-gray-200 rounded-full h-2" data-testid="expenses-bulk-progress">
                    <div
                      className="bg-red-600 h-2 rounded-full transition-all"
                      style={{ width: `${Math.min(100, (bulkProgress.done / bulkProgress.total) * 100)}%` }}
                    />
                  </div>
                )}

                {bulkResult && (
                  <div className="border-t pt-4">
                    {bulkResult.error ? (
                      <div className="text-red-700 text-sm">
                        <p className="font-semibold">Upload failed:</p>
                        <p className="mt-1">{bulkResult.error}</p>
                      </div>
                    ) : (
                      <div className="text-sm">
                        <p className="font-semibold mb-2">
                          ✓ {bulkResult.successful || 0} created
                          {bulkResult.skipped ? `, ${bulkResult.skipped} skipped (duplicate ref)` : ''}
                          {bulkResult.failed ? `, ${bulkResult.failed} failed` : ''}
                          {' '}out of {bulkResult.total} row(s).
                        </p>
                        {bulkResult.failed > 0 && (
                          <div className="max-h-48 overflow-y-auto bg-red-50 border border-red-200 rounded p-2 text-xs">
                            <p className="font-medium text-red-800 mb-1">Failures:</p>
                            {(bulkResult.details || [])
                              .filter(d => d.status === 'failed')
                              .map((d, i) => (
                                <div key={i} className="text-red-700">
                                  Row {d.row}: {d.message}
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-red-600 hover:bg-red-700">
              <Plus size={20} className="mr-2" />
              Add Expense
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit Expense' : 'Add New Expense'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="e.g., Electricity Bill"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date *
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    required
                    data-testid="expense-date-picker"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Category</option>
                  {expenseCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Method *
                </label>
                <select
                  value={formData.payment_method}
                  onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                >
                  <option value="Cash">Cash</option>
                  <option value="Card">Card</option>
                  <option value="Online">Online</option>
                  <option value="Cheque">Cheque</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                </select>
              </div>
              <BankSelector 
                paymentMode={formData.payment_method}
                selectedBank={selectedBank}
                onBankChange={setSelectedBank}
                required={true}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  rows="3"
                  placeholder="Additional details"
                />
              </div>
              <div className="flex gap-3 justify-end pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setDialogOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-red-600 hover:bg-red-700">
                  {editingId ? 'Update' : 'Add'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-red-100 rounded-lg">
              <DollarSign className="text-red-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Expenses</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalExpense)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <TrendingDown className="text-blue-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Transactions</p>
              <p className="text-2xl font-bold text-gray-900">{totalItems || expenses.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <DollarSign className="text-purple-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Average Expense</p>
              <p className="text-2xl font-bold text-gray-900">
                {totalItems > 0 ? formatCurrency(totalExpense / totalItems) : '₹0.00'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Expense List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Method
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {expenses.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    No expense records found. Add your first expense.
                  </td>
                </tr>
              ) : (
                expenses.map((expense) => (
                  <tr key={expense.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{expense.expense_date || expense.date || '-'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{expense.title}</div>
                      {expense.description && (
                        <div className="text-sm text-gray-500">{expense.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800">
                        {expense.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-red-600">
                        {formatCurrency(expense.amount)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{expense.payment_method}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(expense)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(expense.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil(totalItems / itemsPerPage)}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={(page) => setCurrentPage(page)}
          onItemsPerPageChange={(val) => { setItemsPerPage(val); setCurrentPage(1); }}
        />
      </div>
    </div>
  );
};

export default ExpenseManagement;
